import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicImage } from '@/utils/imageUtils';

export interface AdItem {
  id: string;
  title: string;
  description?: string;
  logoUrl: string;
  youtubeId: string;
  clientType?: string;
  industry?: string;
  url_slug?: string;
  type?: 'video' | 'image';
  images?: string[];
}

interface AdGalleryProps {
  ads: AdItem[];
  className?: string;
  slugParam?: string | null;
  navigate?: (path: string, options?: { replace?: boolean }) => void;
  onAdSelect?: (ad: AdItem | null) => void;
}

export function AdGallery({ ads, className, slugParam, navigate, onAdSelect }: AdGalleryProps) {
  const [selectedAd, setSelectedAd] = useState<AdItem | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Handle slug parameter to open specific ad
  useEffect(() => {
    if (slugParam && ads.length > 0) {
      const adBySlug = ads.find(ad => {
        return ad.url_slug === slugParam;
      });
      
      if (adBySlug) {
        setSelectedAd(adBySlug);
        onAdSelect?.(adBySlug);
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugParam, ads]);

  const openModal = (ad: AdItem) => {
    setSelectedAd(ad);
    setSelectedImageIndex(0);
    onAdSelect?.(ad);
    if (navigate) {
      const newUrl = `/ai-ad-gallery?slug=${ad.url_slug || `ad-${ad.id}`}`;
      navigate(newUrl, { replace: true });
    }
  };

  const closeModal = () => {
    setSelectedAd(null);
    onAdSelect?.(null);
    if (navigate) {
      navigate('/ai-ad-gallery', { replace: true });
    }
  };

  const navigateAd = (direction: 'prev' | 'next') => {
    if (!selectedAd) return;
    
    const currentIndex = ads.findIndex(ad => ad.id === selectedAd.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : ads.length - 1;
    } else {
      newIndex = currentIndex < ads.length - 1 ? currentIndex + 1 : 0;
    }
    
    const newAd = ads[newIndex];
    setSelectedAd(newAd);
    setSelectedImageIndex(0);
    onAdSelect?.(newAd);
    if (navigate) {
      const newUrl = `/ai-ad-gallery?slug=${newAd.url_slug || `ad-${newAd.id}`}`;
      navigate(newUrl, { replace: true });
    }
  };

  const getYouTubeThumbnail = (youtubeId: string) => {
    // Extract video ID from embed URL or use as-is if it's already a video ID
    let videoId = youtubeId;
    if (youtubeId.includes('/embed/')) {
      videoId = youtubeId.split('/embed/')[1]?.split('?')[0] || youtubeId;
    } else if (youtubeId.includes('watch?v=')) {
      videoId = youtubeId.split('v=')[1]?.split('&')[0] || youtubeId;
    } else if (youtubeId.includes('youtube.com/shorts/')) {
      videoId = youtubeId.split('shorts/')[1]?.split('?')[0] || youtubeId;
    }
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1] as const,
      },
    },
  };

  const getCardBackgroundImage = (ad: AdItem) => {
    if (ad.type === 'image' && ad.images && ad.images.length > 0) {
      return getPublicImage('frontend-files', ad.images[0]);
    }
    return getYouTubeThumbnail(ad.youtubeId);
  };

  const getGalleryImages = (ad: AdItem): string[] => {
    if (ad.type === 'image' && ad.images && ad.images.length > 0) {
      return ad.images.map(img => getPublicImage('frontend-files', img));
    }
    // Fallback: show logo as single image if no images array
    return [getPublicImage('frontend-files', ad.logoUrl)];
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Ad Grid */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 "
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {ads.map((ad) => (
          <motion.div
            key={ad.id}
            variants={itemVariants}
            whileHover={{ 
              scale: 1.03,
              y: -5,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
          >
            <Card
              className="group cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary/20 bg-card/50 backdrop-blur-sm hover:bg-card/80 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10"
              onClick={() => openModal(ad)}
            >
              <CardContent className="p-0">
                <div className="relative aspect-square overflow-hidden rounded-lg">
                  {/* Card Background (video thumbnail or first image) */}
                  <img
                    src={getCardBackgroundImage(ad)}
                    alt={ad.title}
                    className="w-full h-full object-cover transition-all duration-300 filter grayscale group-hover:grayscale-0"
                    loading="lazy"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  
                  {/* Company Logo - Top Right Corner (only for video-type ads) */}
                  {ad.type !== 'image' && (
                  <div className="absolute top-2 right-2 md:top-3 md:right-3 group-hover:opacity-0 transition-all duration-300">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-white/20">
                      <img
                        src={getPublicImage('frontend-files', ad.logoUrl)}
                        alt={`${ad.title} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  )}
                  
                  {/* Title on Hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <h3 className="font-bold text-lg md:text-2xl text-white text-center px-4 line-clamp-2">
                      {ad.title}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Modal */}
      <Dialog open={!!selectedAd} onOpenChange={() => closeModal()}>
        <DialogContent 
          className="w-full max-w-5xl lg:max-w-6xl max-h-[86vh] p-0 overflow-hidden [&>button]:hidden [&>div]:p-0 [&>div]:pt-0"
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking on share sidebar
            const target = e.target as Element;
            if (target.closest('[data-share-sidebar]')) {
              e.preventDefault();
            }
          }}
        >
          {selectedAd && (
            <>
              <DialogTitle className="sr-only">{selectedAd.title}</DialogTitle>
              
              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold">{selectedAd.title}</h2>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigateAd('prev')}
                    disabled={ads.length <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigateAd('next')}
                    disabled={ads.length <= 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={closeModal}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex flex-col lg:flex-row overflow-auto">
                {/* Media Area: video or image gallery */}
                <div className="flex-1 flex items-start justify-center bg-black p-3 md:p-4">
                  {selectedAd.type === 'image' ? (
                    <div className="w-full max-w-4xl">
                      {/* Main preview image */}
                      <div className="relative w-full bg-black mb-4">
                        {getGalleryImages(selectedAd)[selectedImageIndex] && (
                          <img
                            src={getGalleryImages(selectedAd)[selectedImageIndex]}
                            alt={`${selectedAd.title} preview ${selectedImageIndex + 1}`}
                            className="w-full max-h-[80vh] object-contain transition-transform duration-300 hover:scale-[1.02] cursor-zoom-in"
                          />
                        )}
                      </div>

                      {/* Thumbnails */}
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {getGalleryImages(selectedAd).map((img, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedImageIndex(idx)}
                            className={`relative flex-shrink-0 border rounded-md overflow-hidden ${idx === selectedImageIndex ? 'border-primary' : 'border-border'}`}
                          >
                            <img
                              src={img}
                              alt={`${selectedAd.title} thumbnail ${idx + 1}`}
                              className="h-16 w-24 object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <iframe
                    src={selectedAd.youtubeId}
                    title={selectedAd.title}
                    className="w-full aspect-video max-h-[80vh] md:max-h-[82vh]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  )}
                </div>

                {/* Details / Actions */}
                <div className="lg:w-80 p-4 bg-background border-l space-y-4">
                  {selectedAd.type === 'image' ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        View and download the images from this ad concept.
                      </p>
                      <div className="flex">
                        <a
                          href={getGalleryImages(selectedAd)[selectedImageIndex]}
                          download
                          className="inline-flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-md bg-primary text-primary-foreground border border-primary hover:bg-primary/90 transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          Download image
                        </a>
                      </div>
                    </div>
                  ) : (
                    <>
                  <div>
                    <h3 className="font-semibold mb-2">Description</h3>
                    <p className="text-muted-foreground text-sm">
                      {selectedAd.description || 'No description available.'}
                    </p>
                  </div>

                  {selectedAd.industry && (
                    <div>
                      <h3 className="font-semibold mb-2">Industry</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedAd.industry.split(',').map((tag, index) => (
                          <div key={index} className="inline-block px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm">
                            {tag.trim()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}